import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ArrowLeft, Calendar, DollarSign, Mail, User, Users, FileText, Wrench, LogOut } from "lucide-react";
import ChangePasswordDialog from "@/components/ChangePasswordDialog";
import { toast } from "sonner";

function fmtDate(iso) { if (!iso) return "—"; try { return new Date(iso).toLocaleDateString("en-US"); } catch { return iso; } }
function fmtMoney(n) { return `$${(Number(n) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`; }

export default function AdminCompany() {
  const { id } = useParams();
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [days, setDays] = useState(14);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const { data } = await api.get(`/admin/companies/${id}`);
    setData(data);
  };
  useEffect(() => { load(); }, [id]); // eslint-disable-line

  const extend = async () => {
    const d = Number(days);
    if (!Number.isFinite(d) || d < 1 || d > 365) { toast.error("Days must be between 1 and 365"); return; }
    setSaving(true);
    try {
      await api.post(`/admin/companies/${id}/extend-trial`, { days: d });
      toast.success(`Trial extended by ${d} days`);
      await load();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const handleLogout = () => { logout(); nav("/login"); };

  if (!data) return <div className="p-6 text-slate-500">Loading…</div>;
  const c = data.company;

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-10 bg-slate-900 text-white px-4 sm:px-6 h-14 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-lg bg-blue-600 flex items-center justify-center"><Wrench className="w-4 h-4" /></div>
          <div className="font-bold" style={{ fontFamily: "Outfit" }}>PlumberHQ · Admin</div>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <span className="text-slate-400 hidden sm:inline">{user?.email}</span>
          <ChangePasswordDialog />
          <Button onClick={handleLogout} variant="outline" className="h-9 bg-transparent text-white border-slate-700 hover:bg-slate-800 rounded-lg" data-testid="admin-logout"><LogOut className="w-4 h-4 mr-1" /> Log out</Button>
        </div>
      </header>

      <div className="max-w-5xl mx-auto p-4 sm:p-6 space-y-5">
        <Link to="/admin" className="inline-flex items-center text-sm text-slate-600 font-semibold"><ArrowLeft className="w-4 h-4 mr-1" /> Back to admin</Link>

        <div className="bg-white rounded-2xl border border-slate-200 p-5 sm:p-6">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <h1 className="text-2xl font-bold" data-testid="admin-company-name">{c.name}</h1>
              <p className="text-slate-500 text-sm mt-1">{c.owner_name} · {c.owner_email}</p>
            </div>
            <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase ${c.subscription_status === "active" ? "bg-green-100 text-green-800" : c.subscription_status === "expired" ? "bg-red-100 text-red-800" : "bg-blue-100 text-blue-800"}`}>
              {c.subscription_status}
            </span>
          </div>

          <div className="grid sm:grid-cols-2 gap-3 mt-4 text-sm">
            <Info icon={Calendar} label="Signed up" value={fmtDate(c.created_at)} />
            <Info icon={Calendar} label="Trial ends" value={fmtDate(c.trial_ends_at)} testId="admin-trial-ends" />
            <Info icon={Calendar} label="Subscription ends" value={fmtDate(c.subscription_ends_at)} />
            <Info icon={Calendar} label="Last activity" value={fmtDate(c.last_activity_at)} />
            <Info icon={Users} label="Users" value={c.users} />
            <Info icon={Wrench} label="Jobs" value={c.jobs} />
            <Info icon={FileText} label="Invoices" value={c.invoices} />
            <Info icon={DollarSign} label="Days remaining" value={c.days_remaining} />
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 p-5 sm:p-6">
          <h2 className="text-lg font-bold mb-3" style={{ fontFamily: "Outfit" }}>Extend trial</h2>
          <p className="text-sm text-slate-500 mb-3">Grant this company extra trial days (useful for sales conversations). Won't shorten if the trial is currently longer.</p>
          <div className="flex gap-2 items-end">
            <div className="flex-1">
              <Input type="number" min="1" max="365" value={days} onChange={(e) => setDays(e.target.value)} className="h-11" data-testid="extend-days" />
            </div>
            <Button onClick={extend} disabled={saving} className="h-11 px-4 bg-blue-600 hover:bg-blue-700 font-semibold rounded-xl" data-testid="extend-btn">
              {saving ? "Extending…" : `+${days} days`}
            </Button>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 p-5 sm:p-6">
          <h2 className="text-lg font-bold mb-3" style={{ fontFamily: "Outfit" }}>Team ({data.users.length})</h2>
          <div className="space-y-2">
            {data.users.map((u) => (
              <div key={u.id} className="flex items-center gap-3 text-sm py-1.5">
                <div className="h-8 w-8 rounded-full bg-slate-100 flex items-center justify-center"><User className="w-4 h-4 text-slate-500" /></div>
                <div className="min-w-0 flex-1">
                  <div className="font-semibold truncate">{u.full_name}</div>
                  <div className="text-xs text-slate-500 truncate flex items-center gap-1"><Mail className="w-3 h-3" />{u.email}</div>
                </div>
                <span className="text-xs font-bold uppercase tracking-wide text-slate-600">{u.role}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 p-5 sm:p-6">
          <h2 className="text-lg font-bold mb-3" style={{ fontFamily: "Outfit" }}>Payment history</h2>
          {data.payments.length === 0 ? (
            <p className="text-slate-500 text-sm">No subscription payments yet.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-xs uppercase text-slate-500 border-b border-slate-200"><tr><th className="text-left py-2">Date</th><th className="text-left py-2">Order</th><th className="text-left py-2">Coupon</th><th className="text-right py-2">Amount</th></tr></thead>
              <tbody>
                {data.payments.map((p) => (
                  <tr key={p.id} className="border-b border-slate-100" data-testid={`payment-row-${p.order_id}`}>
                    <td className="py-2">{fmtDate(p.created_at)}</td>
                    <td className="py-2 font-mono text-xs text-slate-500">{p.order_id}</td>
                    <td className="py-2">{p.coupon_code ? <span className="inline-block px-2 py-0.5 rounded-full text-[11px] font-bold uppercase bg-emerald-100 text-emerald-800 font-mono" data-testid={`payment-coupon-${p.order_id}`}>{p.coupon_code}</span> : <span className="text-slate-400">—</span>}</td>
                    <td className="py-2 text-right font-semibold">{fmtMoney(p.amount_usd)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

function Info({ icon: Icon, label, value, testId }) {
  return (
    <div className="flex items-start gap-2.5">
      <div className="h-8 w-8 rounded-lg bg-slate-100 text-slate-600 flex items-center justify-center flex-shrink-0"><Icon className="w-4 h-4" /></div>
      <div className="min-w-0">
        <div className="text-xs text-slate-500 font-semibold uppercase tracking-wide">{label}</div>
        <div className="text-sm text-slate-900 font-medium truncate" data-testid={testId}>{value}</div>
      </div>
    </div>
  );
}
